import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, FileSignature, Loader2, ShieldCheck, CheckCircle } from 'lucide-react';
import { auth, db } from '../../utils/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import SignaturePad from '../engagement/SignaturePad';
import { DESIGN_FEE_TERMS } from '../../types';
import { logPortalActivity } from '../../utils/portalActivityLogger';
import { snapshotShowsDesignFeePaid } from '../../utils/portalDesignFeePaid';
import './DesignFeeSignModal.css';

interface DesignService {
  id: string;
  name: string;
  price: number;
}

interface DesignFeeSignModalProps {
  open: boolean;
  onClose: () => void;
  portalId: string;
  portalSlug: string;
  engagementLetterId?: string;
  selectedServices: DesignService[];
  totalAmount: number;
  clientName: string;
  projectId?: string;
  onAgreementSigned?: () => void;
  onPaymentComplete?: (amountPaid: number) => void;
}

const DesignFeeSignModal: React.FC<DesignFeeSignModalProps> = ({
  open,
  onClose,
  portalId,
  portalSlug,
  engagementLetterId,
  selectedServices,
  totalAmount,
  clientName,
  projectId,
  onAgreementSigned,
  onPaymentComplete,
}) => {
  const [signatureData, setSignatureData] = useState<{ dataUrl: string; signedBy: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waitingForPayment, setWaitingForPayment] = useState(false);
  const [paymentReceived, setPaymentReceived] = useState(false);
  const unsubRef = useRef<(() => void) | null>(null);
  const checkoutWindowRef = useRef<Window | null>(null);
  const checkoutUrlRef = useRef<string | null>(null);

  // Clean up listener on unmount
  useEffect(() => {
    return () => { unsubRef.current?.(); };
  }, []);

  const handleSign = useCallback((data: { dataUrl: string; signedBy: string }) => {
    setSignatureData(data);
  }, []);

  const handleSubmit = async () => {
    if (!signatureData) return;
    setSubmitting(true);
    setError(null);

    try {
      const functionsBase = process.env.REACT_APP_FUNCTIONS_URL
        || `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID || 'lctf-projects'}.cloudfunctions.net`;

      // Agreement fields are written server-side by createDesignFeeCheckout (Admin SDK)
      // after validating the portal PIN/stakeholder claim — guests cannot write agreementSigned*.
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error('Sign in required to continue to checkout');
      }
      const resp = await fetch(`${functionsBase}/createDesignFeeCheckout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          portalId,
          portalSlug,
          engagementLetterId: engagementLetterId || null,
          services: selectedServices.map(s => ({ id: s.id, name: s.name, amount: s.price })),
          totalAmount,
          signature: signatureData,
          projectId: projectId || null,
        }),
      });

      const result = await resp.json();

      if (!resp.ok || !result.url) {
        throw new Error(result.error || 'Failed to create checkout session');
      }

      onAgreementSigned?.();
      logPortalActivity(portalId, projectId || null, 'action', 'signed_agreement', { signedBy: signatureData.signedBy, totalAmount });

      // Open Stripe in a new tab and listen for payment
      checkoutUrlRef.current = result.url;
      checkoutWindowRef.current = window.open(result.url, '_blank');
      setWaitingForPayment(true);
      setSubmitting(false);

      // Listen for real-time payment updates from webhook.
      // Skip the initial snapshot (which reflects pre-existing state)
      // and only react to subsequent updates from the webhook.
      let isFirstSnapshot = true;
      unsubRef.current = onSnapshot(doc(db, 'clientPortals', portalId), (snap) => {
        if (isFirstSnapshot) {
          isFirstSnapshot = false;
          return;
        }
        const data = snap.data();
        const paid = snapshotShowsDesignFeePaid(data, projectId ?? null);
        if (paid) {
          const amount = projectId
            ? (data?.projects?.[projectId]?.amountPaid || data?.amountPaid || totalAmount)
            : (data?.amountPaid || totalAmount);
          setPaymentReceived(true);
          setWaitingForPayment(false);
          unsubRef.current?.();
          unsubRef.current = null;
          onPaymentComplete?.(amount);
        }
      });
    } catch (err) {
      console.error('[DesignFeeSignModal] Checkout error:', err);
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="dfs-overlay" onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div className="dfs-modal" role="dialog" aria-modal="true" aria-label="Sign & Pay">
        {/* Header */}
        <div className="dfs-header">
          <div className="dfs-header-left">
            <FileSignature size={18} />
            <h2>{paymentReceived ? 'Payment Received' : waitingForPayment ? 'Completing Payment' : 'Sign & Pay'}</h2>
          </div>
          <button className="dfs-close" onClick={onClose} disabled={submitting} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {paymentReceived ? (
          <>
            <div className="dfs-body">
              <div className="dfs-payment-success">
                <CheckCircle size={48} className="dfs-success-icon" />
                <h3>Thank you for your payment!</h3>
                <p>We've received your payment of <strong>${totalAmount.toLocaleString()}</strong>. Your project is now moving to the 3D Model phase.</p>
                <p className="dfs-success-sub">You can close this window — your portal has been updated.</p>
              </div>
            </div>
            <div className="dfs-footer">
              <button className="dfs-submit dfs-submit-success" onClick={onClose}>
                <CheckCircle size={16} />
                Done
              </button>
            </div>
          </>
        ) : waitingForPayment ? (
          <>
            <div className="dfs-body">
              <div className="dfs-payment-waiting">
                <Loader2 size={40} className="dfs-spin" />
                <h3>Complete your payment</h3>
                <p>A Stripe Checkout window has been opened. Please complete your payment there.</p>
                <p className="dfs-waiting-sub">This page will update automatically once your payment is confirmed.</p>
              </div>
            </div>
            <div className="dfs-footer" style={{ display: 'flex', gap: 8 }}>
              <button
                className="dfs-submit dfs-submit-secondary"
                style={{ flex: 1 }}
                onClick={onClose}
              >
                Close
              </button>
              <button
                className="dfs-submit"
                style={{ flex: 1 }}
                onClick={() => {
                  if (checkoutWindowRef.current && !checkoutWindowRef.current.closed) {
                    checkoutWindowRef.current.focus();
                  } else if (checkoutUrlRef.current) {
                    checkoutWindowRef.current = window.open(checkoutUrlRef.current, '_blank');
                  }
                }}
              >
                Return to Checkout
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Scrollable content */}
            <div className="dfs-body">
              {/* Order Summary */}
              <div className="dfs-section">
                <h3 className="dfs-section-title">Order Summary</h3>
                <div className="dfs-services">
                  {selectedServices.map(svc => (
                    <div key={svc.id} className="dfs-service-row">
                      <span>{svc.name}</span>
                      <span className="dfs-service-price">${svc.price.toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="dfs-service-total">
                    <span>Total</span>
                    <span>${totalAmount.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="dfs-section">
                <h3 className="dfs-section-title">Terms & Conditions</h3>
                <div className="dfs-terms-scroll">
                  {DESIGN_FEE_TERMS.map((term, i) => (
                    <div key={i} className="dfs-term">
                      <strong>{term.title}</strong>
                      <p>{term.content}</p>
                    </div>
                  ))}
                </div>
                <p className="dfs-terms-consent-note">By signing below, you agree to these Terms & Conditions.</p>
              </div>

              {/* Signature */}
              <div className="dfs-section">
                <h3 className="dfs-section-title">Signature</h3>
                {signatureData ? (
                  <div className="dfs-signature-done">
                    <ShieldCheck size={16} />
                    <span>Signed by <strong>{signatureData.signedBy}</strong></span>
                    <button className="dfs-re-sign" onClick={() => setSignatureData(null)}>Re-sign</button>
                  </div>
                ) : (
                  <SignaturePad onSign={handleSign} initialName={clientName} disabled={submitting} hideSubmitButton />
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="dfs-error">{error}</div>
              )}
            </div>

            {/* Footer */}
            <div className="dfs-footer">
              <button
                className="dfs-submit"
                onClick={handleSubmit}
                disabled={!signatureData || submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="dfs-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FileSignature size={16} />
                    Pay ${totalAmount.toLocaleString()}
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DesignFeeSignModal;
